import { createPortal } from "react-dom";
import { Box, Tooltip } from "@mui/material";
import { useOverlayTransform } from "./useOverlayTransform";

const LEG = 180;
const M = 18;
const SVG_W = LEG + M * 2;
const SVG_H = LEG + M * 2;

// Vertices (with margin offset so handles don't clip)
const AX = M, AY = LEG + M;       // bottom-left, 45°
const BX = LEG + M, BY = LEG + M; // bottom-right, 90°
const CX = LEG + M, CY = M;       // top-right, 45°

const TICK_INTERVAL = 10; // px between minor ticks

function SetSquareSvg() {
  const ticks = [];

  // Ticks along AB (horizontal bottom leg, A→B)
  const abLen = BX - AX;
  for (let px = 0; px <= abLen; px += TICK_INTERVAL) {
    const x = AX + px;
    const isMajor = px % 50 === 0;
    const isMid = !isMajor && px % 25 === 0;
    const tickH = isMajor ? 12 : isMid ? 8 : 4;
    ticks.push(
      <line key={`abT${px}`} x1={x} y1={AY} x2={x} y2={AY - tickH} stroke="#1565c0" strokeWidth={isMajor ? 1.5 : 1} />
    );
    if (isMajor && px > 0) {
      ticks.push(
        <text key={`abL${px}`} x={x} y={AY - 15} fill="#1565c0" fontSize="9" fontWeight="700" textAnchor="middle">
          {px / 10}
        </text>
      );
    }
  }

  // Ticks along BC (vertical right leg, B→C, going upward)
  const bcLen = BY - CY;
  for (let px = 0; px <= bcLen; px += TICK_INTERVAL) {
    const y = BY - px;
    const isMajor = px % 50 === 0;
    const isMid = !isMajor && px % 25 === 0;
    const tickW = isMajor ? 12 : isMid ? 8 : 4;
    ticks.push(
      <line key={`bcT${px}`} x1={BX} y1={y} x2={BX - tickW} y2={y} stroke="#1565c0" strokeWidth={isMajor ? 1.5 : 1} />
    );
    if (isMajor && px > 0) {
      ticks.push(
        <text key={`bcL${px}`} x={BX - 16} y={y + 4} fill="#1565c0" fontSize="9" fontWeight="700" textAnchor="end">
          {px / 10}
        </text>
      );
    }
  }

  return (
    <svg
      width={SVG_W}
      height={SVG_H}
      viewBox={`0 0 ${SVG_W} ${SVG_H}`}
      style={{ display: "block", overflow: "visible" }}
    >
      {/* Triangle fill */}
      <polygon
        points={`${AX},${AY} ${BX},${BY} ${CX},${CY}`}
        fill="rgba(173,216,230,0.40)"
        stroke="#1565c0"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      {/* Right-angle box at B */}
      <rect x={BX - 14} y={BY - 14} width={14} height={14} fill="none" stroke="#1565c0" strokeWidth="1.5" />
      {/* Angle labels */}
      <text x={AX + 18} y={AY - 8} fill="#0d47a1" fontSize="11" fontWeight="800">45°</text>
      <text x={BX - 52} y={BY - 18} fill="#0d47a1" fontSize="11" fontWeight="800">90°</text>
      <text x={CX - 38} y={CY + 20} fill="#0d47a1" fontSize="11" fontWeight="800">45°</text>
      {ticks}
    </svg>
  );
}

export default function VirtualSetSquareOverlay({ visible }) {
  const { transform, onPointerDownMove, onPointerDownRotate, onPointerDownScale } =
    useOverlayTransform();

  if (!visible) return null;

  return createPortal(
    <Box
      sx={{
        position: "fixed",
        left: transform.x,
        top: transform.y,
        transform: `translate(-50%, -50%) rotate(${transform.rotation}deg) scale(${transform.scale})`,
        transformOrigin: "center center",
        zIndex: 9999,
        pointerEvents: "auto",
        cursor: "grab",
        touchAction: "none",
        userSelect: "none",
        filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.22))",
      }}
      onPointerDown={onPointerDownMove}
    >
      <SetSquareSvg />
      <Tooltip title="Drag to rotate (hold Shift to snap to 1°)" placement="top">
        <Box
          onPointerDown={onPointerDownRotate}
          sx={{
            position: "absolute",
            left: -14,
            top: SVG_H / 2 - 14,
            width: 28,
            height: 28,
            borderRadius: "50%",
            bgcolor: "#1565c0",
            color: "white",
            fontSize: "0.7rem",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "grab",
            border: "2px solid white",
          }}
        >
          ↻
        </Box>
      </Tooltip>
      <Tooltip title="Drag right/left to resize" placement="top">
        <Box
          onPointerDown={(e) => onPointerDownScale(e, { min: 0.4, max: 3, perPx: 0.007 })}
          sx={{
            position: "absolute",
            right: -14,
            top: SVG_H / 2 - 14,
            width: 28,
            height: 28,
            borderRadius: "6px",
            bgcolor: "#0d47a1",
            color: "white",
            fontSize: "0.85rem",
            fontWeight: 800,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "ew-resize",
            border: "2px solid white",
          }}
        >
          ⇔
        </Box>
      </Tooltip>
    </Box>,
    document.body
  );
}
